import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Search,
  Users,
  Eye,
  Plus,
  SlidersHorizontal,
  X,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStaffList, useStaffRoles } from "@/hooks/useStaffList";
import { useDebounce } from "@/hooks/use-debounce";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { AccessDenied } from "@/components/AccessDenied";
import { getApiErrorStatus } from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

type ColumnKey = "name" | "role" | "designation" | "email" | "phone" | "status";

const ALL_COLUMNS: { key: ColumnKey; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "role", label: "Role" },
  { key: "designation", label: "Designation" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "status", label: "Status" },
];

const getInitials = (first: string, last: string) => {
  return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
};

const getAvatarColor = (name: string) => {
  const colors = [
    "from-emerald-500 to-emerald-600",
    "from-teal-500 to-teal-600",
    "from-success to-success",
    "from-cyan-600 to-cyan-700",
    "from-stone-500 to-stone-600",
    "from-amber-600 to-amber-700",
    "from-slate-500 to-slate-600",
    "from-lime-600 to-lime-700",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const PAGE_SIZE = 20;

export const StaffList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
    new Set(ALL_COLUMNS.map((c) => c.key))
  );

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, statusFilter, debouncedSearch]);

  const { toast } = useToast();
  const toastedError = useRef(false);

  const { data, isLoading, isError, error, refetch } = useStaffList({
    page: currentPage,
    role: roleFilter || undefined,
    search: debouncedSearch || undefined,
  });
  const {
    data: staffRoles,
    isLoading: areStaffRolesLoading,
    isError: areStaffRolesError,
    error: staffRolesError,
    refetch: refetchStaffRoles,
  } = useStaffRoles();

  const isPermissionError =
    getApiErrorStatus(error) === 403 ||
    getApiErrorStatus(staffRolesError) === 403;
  const roleLabels = useMemo(
    () =>
      new Map(
        (staffRoles ?? []).map((role) => [role.name, role.label] as const)
      ),
    [staffRoles]
  );

  useEffect(() => {
    if ((isError || areStaffRolesError) && !toastedError.current) {
      toastedError.current = true;
      toast({
        title: isPermissionError ? "Access Denied" : "Error",
        description: isPermissionError
          ? "You don't have permission to view staff members."
          : areStaffRolesError
            ? "Something went wrong while loading staff roles."
            : "Something went wrong while loading staff members.",
        variant: "destructive",
      });
    }
    if (!isError && !areStaffRolesError) {
      toastedError.current = false;
    }
  }, [areStaffRolesError, isError, isPermissionError, toast]);

  const filteredResults = useMemo(() => {
    if (!data?.results) return [];
    if (!statusFilter) return data.results;
    if (statusFilter === "active")
      return data.results.filter((s) => s.is_active);
    return data.results.filter((s) => !s.is_active);
  }, [data?.results, statusFilter]);

  const totalCount = statusFilter ? filteredResults.length : (data?.count ?? 0);
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setStatusFilter("");
  };

  const hasFilters = searchQuery || roleFilter || statusFilter;

  // Loading state
  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_STAFF)) {
    return (
      <AccessDenied description="You do not have permission to view or manage staff members." />
    );
  }

  if (isLoading || areStaffRolesLoading) {
    return (
      <Card className="flex-1 min-h-0 flex flex-col border-none shadow-none">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-semibold tracking-tight">
                Staff
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Manage your healthcare team members
              </CardDescription>
            </div>
            <Button variant="outline" disabled className="gap-2">
              <Plus className="h-4 w-4" />
              Add New Staff
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="h-10 w-64 bg-muted rounded-md animate-pulse" />
            <div className="flex gap-2">
              <div className="h-9 w-24 bg-muted rounded-md animate-pulse" />
              <div className="h-9 w-24 bg-muted rounded-md animate-pulse" />
            </div>
          </div>
          <div className="flex-1 border rounded-lg bg-muted">
            <div className="p-8 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-8 w-8 bg-muted-foreground/20 rounded-full animate-pulse" />
                  <div className="flex-1 h-4 bg-muted-foreground/20 rounded animate-pulse" />
                  <div className="w-24 h-4 bg-muted-foreground/20 rounded animate-pulse" />
                  <div className="w-32 h-4 bg-muted-foreground/20 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error states
  if ((isError || areStaffRolesError) && isPermissionError) {
    return (
      <AccessDenied description="Your current permissions do not allow access to staff management." />
    );
  }

  if (isError || areStaffRolesError || !staffRoles?.length) {
    return (
      <Card className="h-full flex flex-col items-center justify-center border-none shadow-none">
        <div className="text-center py-12 px-4">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-muted flex items-center justify-center">
            <Users className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="z-h3 text-foreground mb-2">Failed to Load</h3>
          <p className="z-body text-muted-foreground mb-4 max-w-sm mx-auto">
            {areStaffRolesError
              ? "Something went wrong while loading staff roles."
              : "Something went wrong while loading staff members."}
          </p>
          <Button
            variant="outline"
            onClick={() => {
              void refetch();
              void refetchStaffRoles();
            }}
          >
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex-1 min-h-0 flex flex-col border-none shadow-none">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="z-h1 text-foreground">Staff</CardTitle>
            <CardDescription className="text-muted-foreground">
              Manage your healthcare team members
            </CardDescription>
          </div>
          <Button
            onClick={() => navigate("/settings/staff/create-staff")}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add New Staff
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 flex flex-col gap-4 p-0">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 bg-background border-input focus:border-primary"
              />
            </div>

            {/* Status Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-10 gap-1.5 px-3",
                    statusFilter && "border-primary text-primary bg-primary/5"
                  )}
                >
                  {statusFilter
                    ? STATUS_OPTIONS.find((s) => s.value === statusFilter)
                        ?.label
                    : "Status"}
                  {statusFilter ? (
                    <X
                      className="h-3.5 w-3.5 ml-0.5"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setStatusFilter("");
                      }}
                    />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 ml-0.5 opacity-60" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-40">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() =>
                      setStatusFilter(
                        statusFilter === opt.value ? "" : opt.value
                      )
                    }
                    className={cn(
                      "w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors",
                      statusFilter === opt.value && "text-primary font-medium"
                    )}
                  >
                    {opt.label}
                    {statusFilter === opt.value && (
                      <X className="h-3 w-3 opacity-60" />
                    )}
                  </button>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Role Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-10 gap-1.5 px-3",
                    roleFilter && "border-primary text-primary bg-primary/5"
                  )}
                >
                  {roleFilter
                    ? (roleLabels.get(roleFilter) ?? roleFilter)
                    : "Role"}
                  {roleFilter ? (
                    <X
                      className="h-3.5 w-3.5 ml-0.5"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRoleFilter("");
                      }}
                    />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 ml-0.5 opacity-60" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {staffRoles.map((role) => (
                  <button
                    key={role.name}
                    onClick={() =>
                      setRoleFilter(roleFilter === role.name ? "" : role.name)
                    }
                    className={cn(
                      "w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors",
                      roleFilter === role.name && "text-primary font-medium"
                    )}
                  >
                    {role.label}
                    {roleFilter === role.name && (
                      <X className="h-3 w-3 opacity-60" />
                    )}
                  </button>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Clear all */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Column visibility toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-10 gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                <span className="hidden sm:inline">Columns</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {ALL_COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={visibleColumns.has(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 px-6 flex flex-col">
          {filteredResults.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center border border-dashed rounded-xl bg-muted">
              <div className="w-16 h-16 rounded-2xl bg-background flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-muted-foreground/50" />
              </div>
              <h3 className="z-h3 text-foreground mb-1">No Staff Found</h3>
              <p className="z-body text-muted-foreground">
                {hasFilters
                  ? "No staff members match your current filters."
                  : "Get started by adding your first staff member."}
              </p>
              {!hasFilters && (
                <Button
                  onClick={() => navigate("/settings/staff/create-staff")}
                  className="mt-4 gap-2"
                  variant="outline"
                >
                  <Plus className="h-4 w-4" />
                  Add New Staff
                </Button>
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto rounded-3xl border border-border/60 bg-card shadow-sm">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow className="hover:bg-card">
                    {visibleColumns.has("name") && (
                      <TableHead className="z-label uppercase tracking-wide text-muted-foreground pl-4">
                        Name
                      </TableHead>
                    )}
                    {visibleColumns.has("role") && (
                      <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                        Role
                      </TableHead>
                    )}
                    {visibleColumns.has("designation") && (
                      <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                        Designation
                      </TableHead>
                    )}
                    {visibleColumns.has("email") && (
                      <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                        Email
                      </TableHead>
                    )}
                    {visibleColumns.has("phone") && (
                      <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                        Phone
                      </TableHead>
                    )}
                    {visibleColumns.has("status") && (
                      <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                        Status
                      </TableHead>
                    )}
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.map((staff, index) => {
                    const fullName = `${staff.first_name} ${staff.last_name}`;

                    return (
                      <TableRow
                        key={staff.id}
                        className="group bg-card transition-colors hover:bg-muted/20 cursor-pointer"
                        style={{ animationDelay: `${index * 30}ms` }}
                        onClick={() =>
                          navigate(`/settings/staff/edit-staff?id=${staff.id}`)
                        }
                      >
                        {visibleColumns.has("name") && (
                          <TableCell className="pl-4">
                            <div className="flex items-center gap-3">
                              {staff.profile_pic ? (
                                <img
                                  src={staff.profile_pic}
                                  alt={fullName}
                                  className="w-9 h-9 rounded-full object-cover ring-2 ring-background shadow-sm"
                                />
                              ) : (
                                <div
                                  className={cn(
                                    "w-9 h-9 rounded-full bg-gradient-to-br text-white flex items-center justify-center text-xs font-semibold shadow-sm",
                                    getAvatarColor(fullName)
                                  )}
                                >
                                  {getInitials(
                                    staff.first_name,
                                    staff.last_name
                                  )}
                                </div>
                              )}
                              <span className="z-body-md text-foreground">
                                {fullName}
                              </span>
                            </div>
                          </TableCell>
                        )}

                        {visibleColumns.has("role") && (
                          <TableCell>
                            <span className="z-body text-muted-foreground">
                              {roleLabels.get(staff.user_type__name) ??
                                staff.user_type__name}
                            </span>
                          </TableCell>
                        )}

                        {visibleColumns.has("designation") && (
                          <TableCell>
                            {staff.console_user_profile__designation ? (
                              <span className="z-body text-muted-foreground">
                                {staff.console_user_profile__designation}
                              </span>
                            ) : (
                              <span className="z-body text-muted-foreground/40">
                                —
                              </span>
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.has("email") && (
                          <TableCell>
                            {staff.email ? (
                              <span className="z-body text-muted-foreground">
                                {staff.email}
                              </span>
                            ) : (
                              <span className="z-body text-muted-foreground/40">
                                —
                              </span>
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.has("phone") && (
                          <TableCell>
                            {staff.phone_number ? (
                              <span className="z-body text-muted-foreground font-mono">
                                {staff.phone_number}
                              </span>
                            ) : (
                              <span className="z-body text-muted-foreground/40">
                                —
                              </span>
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.has("status") && (
                          <TableCell>
                            <Badge
                              variant={staff.is_active ? "success" : "critical"}
                              className="font-medium"
                            >
                              {staff.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                        )}

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() =>
                              navigate(
                                `/settings/staff/edit-staff?id=${staff.id}`
                              )
                            }
                          >
                            <Eye className="h-4 w-4" />
                            <span className="sr-only">View profile</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Footer */}
        {data && filteredResults.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <span className="z-secondary text-muted-foreground">
              Showing{" "}
              <span className="z-numeric font-medium text-foreground">
                {filteredResults.length}
              </span>{" "}
              of{" "}
              <span className="z-numeric font-medium text-foreground">
                {totalCount}
              </span>{" "}
              staff
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!data.previous}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="z-secondary text-muted-foreground px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!data.next}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StaffList;
